import { useTranslation } from 'react-i18next'

import { FileCsvIcon, TrashIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { IconButton, Text, UploadFileArea } from '@reveni-io/reveni-ui'

import { csvAcceptedFormats } from './constants'
import { getAcceptAttribute, isAcceptedFormat } from './helpers'
import * as Styles from './styles'

const ImportFileUploader = ({
  onSelectFile,
  file,
  onDeleteFile,
  onError,
  maxSizeInMB = 10,
  placeholder = 'Arrastra tu archivo CSV aquí',
  buttonText = 'Haz click para seleccionarlo desde tu ordenador',
  helperText = 'Solo archivos CSV. Máximo {{maxSizeInMB}} MB',
  acceptedFormats = csvAcceptedFormats,
  invalidFormatText = 'El archivo debe ser de formato CSV.',
  Icon = FileCsvIcon,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const maxSize = maxSizeInMB * 1024 * 1024

  const isValidSize = files => {
    return files.every(file => file.size <= maxSize)
  }

  const isValidFormat = files => {
    return files.every(file => isAcceptedFormat(file, acceptedFormats))
  }

  const validateAndSelectFile = file => {
    if (!isValidFormat(file)) {
      return t(invalidFormatText)
    }

    if (!isValidSize(file)) {
      return t('El archivo no debe superar el límite permitido ({{maxSizeInMB}}MB)', { maxSizeInMB })
    }

    return null
  }

  const handleSelect = files => {
    onError(null)
    onSelectFile(files)
  }

  const handleError = errorMsg => {
    onError(errorMsg)
  }

  return (
    <>
      <UploadFileArea
        onSelect={handleSelect}
        onError={handleError}
        onValidate={validateAndSelectFile}
        accept={getAcceptAttribute(acceptedFormats)}
        placeholder={t(placeholder)}
        placeholderIcon={<Icon size={32} color={theme.colors.text.subtle} />}
        buttonText={t(buttonText)}
      >
        <Text type="bodyXSRegular" color={theme.colors.text.subtle}>
          {t(helperText, { maxSizeInMB })}
        </Text>
      </UploadFileArea>

      {file && (
        <Styles.PreviewContainer key={file?.name}>
          <Icon size={24} color={theme.colors.outline.default} />
          <Text type="bodyMBold" color={theme.colors.outline.default}>
            {file?.name}
          </Text>
          <div data-testid="deleteFile">
            <IconButton
              onClick={() => onDeleteFile(file?.name)}
              variant="text"
              icon={<TrashIcon size={24} color={theme.colors.outline.default} />}
            />
          </div>
        </Styles.PreviewContainer>
      )}
    </>
  )
}

export default ImportFileUploader
